import { QueryClient, useQuery, type QueryFunctionContext } from '@tanstack/react-query'
import { toast } from 'sonner'
import { get, graphql, RateLimitError } from './api'
import {
  CLOSED_ISSUES,
  ORG_ISSUE_FIELDS,
  REPO_META,
  ISSUE_DETAIL,
  MAX_UNIVERSE_PAGES,
} from './github-queries'
import { normalizeIssue, type IssueField, type IssueNode, type WorkIssue } from '@/types/github'

// How often the active view re-checks for upstream changes. The server holds
// the universe in memory (see server/github/universe.ts), so polls are served
// without hitting GitHub at all — the interval is just a freshness target, not
// a budget constraint.
export const ACTIVE_VIEW_REFETCH_MS = 120_000

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // One retry for transient blips, but never on a rate limit — retrying
      // can't succeed until the budget resets, and it just delays the message.
      retry: (failureCount, error) => !(error instanceof RateLimitError) && failureCount < 1,
      refetchOnWindowFocus: true,
    },
  },
})

/** Defaults match production; /api/env overrides at sign-in time. */
export const appConfig = {
  owner: 'n3oltd',
  repo: 'work',
}

export async function loadAppConfig(): Promise<void> {
  const res = await fetch('/api/env')
  if (!res.ok) return
  const env = (await res.json()) as { trustedOrg?: string; repo?: string }
  if (env.trustedOrg) appConfig.owner = env.trustedOrg
  const repoName = env.repo?.split('/')[1]
  if (repoName) appConfig.repo = repoName
}

// Prefixes that also match every entry their builder below creates.
const issueDetails = ['issue-detail'] as const
const savedSearches = ['saved-searches'] as const

export const keys = {
  issues: ['issues'] as const,
  closedIssues: ['closed-issues'] as const,
  issueFields: ['issue-fields'] as const,
  repoMeta: ['repo-meta'] as const,
  issueDetails,
  issueDetail: (number: number) => [...issueDetails, number] as const,
  bodySearch: (q: string) => ['body-search', q] as const,
  crossContextIndex: ['cross-context-index'] as const,
  contextOrder: (context: string) => ['context-order', context] as const,
  savedSearches,
  savedSearchesFor: (login: string) => [...savedSearches, login] as const,
  metrics: (windowDays: number) => ['metrics', windowDays] as const,
  workDocs: ['work-docs'] as const,
  workDoc: (file: string | undefined) => ['work-doc', file] as const,
  issueTemplates: ['issue-templates'] as const,
}

const writtenBehind = new WeakMap<QueryClient, Set<string>>()

export function markWrittenBehind(client: QueryClient, issueId: string): void {
  writtenBehind.set(client, (writtenBehind.get(client) ?? new Set<string>()).add(issueId))
}

/**
 * The whole open-issue universe, shared by every view. GitHub search can't
 * filter on custom Issue Fields, so filtering happens locally (lib/filters).
 * The server walks GitHub; this is one request for its snapshot.
 */
export async function fetchOpenIssues({ client }: Pick<QueryFunctionContext, 'client'>): Promise<WorkIssue[]> {
  const { issues, truncated, owed } = await get<{ issues: IssueNode[]; truncated: boolean; owed: string[] }>(
    '/api/github/universe',
  )
  if (truncated) {
    // Never lie silently — views filter this dataset and trust it's complete.
    toast.warning(`Showing the first ${MAX_UNIVERSE_PAGES * 100} open issues`, {
      id: 'universe-truncated',
      description: 'There are more — counts and views may be incomplete. Tell an admin.',
    })
  }
  const fresh = issues.map(normalizeIssue)
  const ours = writtenBehind.get(client) ?? new Set<string>()
  for (const id of ours) if (!owed.includes(id)) ours.delete(id)
  const cached = client.getQueryData<WorkIssue[]>(keys.issues)
  if (!cached || ours.size === 0) return fresh
  return [...fresh.filter((i) => !ours.has(i.id)), ...cached.filter((i) => ours.has(i.id))]
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

export function useIssues() {
  return useQuery({
    queryKey: keys.issues,
    queryFn: fetchOpenIssues,
    // Pause background freshness while any mutation is in flight — a poll
    // racing an optimistic write would visibly revert it.
    refetchInterval: () => (queryClient.isMutating() > 0 ? false : ACTIVE_VIEW_REFETCH_MS),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: () => queryClient.isMutating() === 0,
  })
}

export function useClosedIssues() {
  return useQuery({
    queryKey: keys.closedIssues,
    staleTime: 60_000,
    queryFn: async (): Promise<WorkIssue[]> => {
      const data = await graphql<{ repository: { issues: { nodes: IssueNode[] } } }>({
        query: CLOSED_ISSUES,
        variables: { owner: appConfig.owner, name: appConfig.repo },
        preview: 'issue_fields',
      })
      return data.repository.issues.nodes.map(normalizeIssue)
    },
  })
}

type FieldsResponse = {
  organization: {
    issueFields: {
      nodes: Array<{
        __typename: string
        id?: string
        name?: string
        options?: Array<{ id: string; name: string; color: string | null }>
      }>
    }
  }
}

const FIELD_TYPE: Record<string, IssueField['dataType']> = {
  IssueFieldSingleSelect: 'single-select',
  IssueFieldNumber: 'number',
  IssueFieldDate: 'date',
  IssueFieldText: 'text',
}

export async function fetchIssueFields(): Promise<IssueField[]> {
  const data = await graphql<FieldsResponse>({
    query: ORG_ISSUE_FIELDS,
    variables: { org: appConfig.owner },
    preview: 'issue_fields',
  })
  return data.organization.issueFields.nodes
    .filter((n) => n.id && n.name)
    .map((n) => ({
      id: n.id!,
      name: n.name!,
      dataType: FIELD_TYPE[n.__typename] ?? 'text',
      options: n.options ?? [],
    }))
}

// Org field options + repo metadata (labels/teams/users) change rarely but
// DO change (a new client, a new area/team label). 5min staleTime so an
// addition shows within minutes / on window focus, rather than up to an hour
// in a long-lived tab; mutations still invalidate eagerly.
const METADATA_STALE_MS = 5 * 60 * 1000

export function useIssueFields() {
  return useQuery({
    queryKey: keys.issueFields,
    queryFn: fetchIssueFields,
    staleTime: METADATA_STALE_MS,
  })
}

export function fieldByName(fields: IssueField[] | undefined, name: string): IssueField | undefined {
  return fields?.find((f) => f.name === name)
}

export function optionNames(fields: IssueField[] | undefined, name: string): string[] {
  return fieldByName(fields, name)?.options.map((o) => o.name) ?? []
}

export type RepoMeta = {
  id: string
  issueTypes: Array<{ id: string; name: string }>
  labels: Array<{ id: string; name: string }>
  assignableUsers: Array<{ id: string; login: string; name: string | null; avatarUrl: string }>
}

export function useRepoMeta() {
  return useQuery({
    queryKey: keys.repoMeta,
    staleTime: METADATA_STALE_MS,
    queryFn: async (): Promise<RepoMeta> => {
      const data = await graphql<{
        repository: {
          id: string
          issueTypes: { nodes: Array<{ id: string; name: string }> }
          labels: { nodes: Array<{ id: string; name: string }> }
          assignableUsers: { nodes: Array<{ id: string; login: string; name: string | null; avatarUrl: string }> }
        }
      }>({ query: REPO_META, variables: { owner: appConfig.owner, name: appConfig.repo } })
      return {
        id: data.repository.id,
        issueTypes: data.repository.issueTypes.nodes,
        labels: data.repository.labels.nodes,
        assignableUsers: data.repository.assignableUsers.nodes,
      }
    },
  })
}

export type IssueComment = {
  id: string
  author: { login: string; avatarUrl: string } | null
  createdAt: string
  body: string
  /** GitHub-rendered HTML; absent on optimistic comments until refetch. */
  bodyHTML?: string | null
}

export type LinkedRef = {
  id: string
  number: number
  title: string
  state: string
  repo: string
  url?: string
}

export type IssueDetail = {
  issue: WorkIssue
  body: string
  /** GitHub-rendered HTML; nulled on optimistic body edits until refetch. */
  bodyHTML: string | null
  subIssues: LinkedRef[]
  linkedPrs: LinkedRef[]
  comments: IssueComment[]
}

export function useIssueDetail(number: number) {
  return useQuery({
    queryKey: keys.issueDetail(number),
    queryFn: async (): Promise<IssueDetail> => {
      const data = await graphql<{
        repository: {
          issue:
            | (IssueNode & {
                body: string | null
                bodyHTML: string | null
                subIssuesList: { nodes: Array<{ id: string; number: number; title: string; state: string; repository: { nameWithOwner: string } }> }
                closedByPullRequestsReferences: { nodes: Array<{ id: string; number: number; title: string; state: string; url: string; repository: { nameWithOwner: string } }> } | null
                timelineItems: { nodes: Array<{ __typename: string; source?: { id?: string; number?: number; title?: string; state?: string; url?: string; repository?: { nameWithOwner: string } } }> }
                commentsPage: { nodes: Array<{ id: string; author: { login: string; avatarUrl: string } | null; createdAt: string; body: string; bodyHTML: string | null }> }
              })
            | null
        }
      }>({
        query: ISSUE_DETAIL,
        variables: { owner: appConfig.owner, name: appConfig.repo, number },
        preview: 'issue_fields',
      })

      const node = data.repository.issue
      if (!node) throw new Error(`issue #${number} not found`)

      const prRefs = new Map<string, LinkedRef>()
      for (const pr of node.closedByPullRequestsReferences?.nodes ?? []) {
        prRefs.set(pr.id, { id: pr.id, number: pr.number, title: pr.title, state: pr.state, url: pr.url, repo: pr.repository.nameWithOwner })
      }
      for (const item of node.timelineItems.nodes) {
        const s = item.source
        if (s?.id && s.number && s.repository) {
          prRefs.set(s.id, {
            id: s.id,
            number: s.number,
            title: s.title ?? '',
            state: s.state ?? '',
            url: s.url,
            repo: s.repository.nameWithOwner,
          })
        }
      }

      return {
        issue: normalizeIssue(node),
        body: node.body ?? '',
        bodyHTML: node.bodyHTML,
        subIssues: node.subIssuesList.nodes.map((s) => ({
          id: s.id,
          number: s.number,
          title: s.title,
          state: s.state,
          repo: s.repository.nameWithOwner,
        })),
        linkedPrs: [...prRefs.values()],
        comments: node.commentsPage.nodes,
      }
    },
  })
}
